import React from 'react';
import dayjs from 'dayjs';
import type { FinalInspectionReport } from '@/services/final-inspection-report.service';
import type { FinalInspectionRequest } from '@/services/qc-workflow.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

function dash(v: string | null | undefined): string {
  if (v == null || String(v).trim() === '') return '—';
  return String(v);
}

function fmtDateTime(v?: string | null): string {
  if (!v) return '';
  return dayjs(v).format('DD MMM YYYY HH:mm');
}

function absFileUrl(path?: string | null): string {
  if (!path) return '—';
  if (path.startsWith('http')) return path;
  if (typeof window !== 'undefined') return `${window.location.origin}${path}`;
  return path;
}

export type FinalInspectionReportPrintViewProps =
  | {
      source: 'qms_report';
      report: FinalInspectionReport;
      printedAt: string;
      resolveUser?: (id?: string | null) => string;
    }
  | {
      source: 'fir_request';
      fir: FinalInspectionRequest;
      printedAt: string;
      resolveUser?: (id?: string | null) => string;
    };

/** Final inspection report — shared pagination engine with restored parameters/results. */
export function FinalInspectionReportPrintView(props: FinalInspectionReportPrintViewProps) {
  const resolve = props.resolveUser ?? ((_id?: string | null) => '—');

  if (props.source === 'qms_report') {
    const r = props.report;
    const equipment =
      r.inspection_equipment?.map((e) => `${e.asset_code} — ${e.asset_name}`).join('; ') || '';

    return (
      <EngineCommercialA4Print
        docType="final_inspection_report"
        title="FINAL INSPECTION REPORT (QMS)"
        documentNumber={r.fir_number || 'FIR'}
        partyName={dash(r.item_name)}
        metaLines={[
          { label: 'FIR number', value: dash(r.fir_number) },
          { label: 'Printed', value: fmtDateTime(props.printedAt) },
          { label: 'Work order', value: dash(r.work_order_number) },
          { label: 'Item', value: dash(r.item_name) },
          { label: 'Inspection date', value: fmtDateTime(r.inspection_date) },
          { label: 'Inspector', value: dash(r.inspector_name) },
          { label: 'Status', value: dash(r.status) },
          { label: 'Approval', value: dash(r.approval_status) },
          { label: 'Approved qty', value: Number(r.approved_quantity ?? 0).toFixed(2) },
          { label: 'Rejected qty', value: Number(r.rejected_quantity ?? 0).toFixed(2) },
          { label: 'Total qty', value: Number(r.total_quantity ?? 0).toFixed(2) },
          { label: 'Inspection criteria', value: r.inspection_criteria?.trim() || '' },
          { label: 'Equipment used', value: equipment },
        ].filter((m) => m.value && m.value !== '—')}
        columns={[
          { key: 'sno', label: '#' },
          { key: 'description', label: 'Parameter' },
          { key: 'spec', label: 'Specification' },
          { key: 'measured', label: 'Measured' },
          { key: 'result', label: 'Result' },
          { key: 'remarks', label: 'Remarks' },
        ]}
        lines={(r.quality_parameters || []).map((p, idx) => ({
          lineNumber: idx + 1,
          description: dash(p.parameter_name),
          cells: {
            spec: dash(p.specification),
            measured: dash(p.measured_value),
            result: dash(p.result),
            remarks: dash(p.remarks),
          },
        }))}
        finals={[
          { id: 'attachments', heightPx: 56 },
          { id: 'approval', heightPx: 56 },
        ]}
        finalBlocks={{
          attachments: (
            <div style={{ fontSize: 11 }}>
              <strong>Attachments:</strong>{' '}
              {r.attachments?.length
                ? r.attachments
                    .map(
                      (a) =>
                        `${dash(a.file_name)} (${dash(a.file_type)}) ${absFileUrl(a.file_url || a.url)}`
                    )
                    .join('; ')
                : 'None'}
            </div>
          ),
          approval: (
            <div style={{ fontSize: 11 }}>
              <p>
                <strong>Approved by:</strong> {resolve(r.approved_by)} ·{' '}
                <strong>Approved at:</strong> {fmtDateTime(r.approved_at) || '—'}
              </p>
              <p>
                <strong>Rejection reason:</strong> {dash(r.rejection_reason)}
              </p>
            </div>
          ),
        }}
      />
    );
  }

  const fir = props.fir;
  const s = String(fir.status ?? '');
  const approvalLabel =
    s === 'Approved' || s === 'Partial'
      ? 'Approved'
      : s === 'Rejected'
        ? 'Rejected'
        : 'Pending approval';
  const lineRows = fir.lineItemResults ?? [];
  const equipment =
    fir.inspection_equipment?.map((e) => `${e.asset_code} — ${e.asset_name}`).join('; ') || '';

  const stageExtra = Array.isArray(fir.stage_summary)
    ? (fir.stage_summary as any[])
        .map((stage) => {
          const insp = stage.inspection;
          return `${dash(stage.stage_name)}${stage.stage_code ? ` (${dash(stage.stage_code)})` : ''} · ${dash(stage.status)} · Seq ${stage.sequence_number ?? '—'} · Insp ${dash(insp?.inspection_number)} ${dash(insp?.result)}`;
        })
        .join('\n')
    : '';

  return (
    <EngineCommercialA4Print
      docType="final_inspection_report"
      title="FINAL INSPECTION REQUEST (WO FIR)"
      documentNumber={fir.fir_number || 'FIR'}
      partyName={dash(fir.workOrder?.salesOrder?.customer_name)}
      metaLines={[
        { label: 'FIR number', value: dash(fir.fir_number) },
        { label: 'Printed', value: fmtDateTime(props.printedAt) },
        { label: 'Work order', value: dash(fir.workOrder?.work_order_number) },
        {
          label: 'Line',
          value: fir.workOrderLineItem?.item_description
            ? `${fir.workOrderLineItem.line_number != null ? `Line ${fir.workOrderLineItem.line_number}: ` : ''}${fir.workOrderLineItem.item_description}`
            : dash(fir.work_order_line_item_id),
        },
        {
          label: 'Customer order',
          value: dash(fir.workOrder?.salesOrder?.sales_order_number),
        },
        {
          label: 'Customer',
          value: dash(fir.workOrder?.salesOrder?.customer_name),
        },
        {
          label: 'Customer PO',
          value: dash(fir.workOrder?.salesOrder?.customer_po_number),
        },
        {
          label: 'Project',
          value: dash(fir.workOrder?.salesOrder?.project_name),
        },
        { label: 'Status', value: dash(fir.status) },
        { label: 'Approval (derived)', value: approvalLabel },
        { label: 'Requested', value: fmtDateTime(fir.requested_date) },
        {
          label: 'Inspector',
          value: dash(fir.inspector?.name ?? fir.inspector_name) || resolve(fir.inspector_id),
        },
        { label: 'Approved qty', value: Number(fir.approved_quantity ?? 0).toFixed(2) },
        { label: 'Rejected qty', value: Number(fir.rejected_quantity ?? 0).toFixed(2) },
        { label: 'Inspection criteria', value: fir.inspection_criteria?.trim() || '' },
        { label: 'Equipment used', value: equipment },
      ].filter((m) => m.value && m.value !== '—')}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Line item' },
        { key: 'approved', label: 'Approved', align: 'right' },
        { key: 'rejected', label: 'Rejected', align: 'right' },
        { key: 'reason', label: 'Reason' },
        { key: 'ncr', label: 'NCR' },
      ]}
      lines={(lineRows.length
        ? lineRows
        : [
            {
              id: 'empty',
              workOrderLineItem: { item_description: 'None recorded' } as any,
              approved_quantity: 0,
              rejected_quantity: 0,
              rejection_reason: '',
              ncr_id: '',
            },
          ]
      ).map((row: any, idx: number) => ({
        lineNumber: idx + 1,
        description: dash(row.workOrderLineItem?.item_description ?? row.work_order_line_item_id),
        cells: {
          approved: Number(row.approved_quantity ?? 0).toFixed(2),
          rejected: Number(row.rejected_quantity ?? 0).toFixed(2),
          reason: dash(row.rejection_reason),
          ncr: dash(row.ncr_id),
        },
      }))}
      finals={[
        ...(stageExtra ? [{ id: 'stages', heightPx: 80 }] : []),
        { id: 'attachments', heightPx: 48 },
        { id: 'approval', heightPx: 56 },
      ]}
      finalBlocks={{
        stages: stageExtra ? (
          <div>
            <strong>Stage summary</strong>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 11 }}>
              {stageExtra}
            </pre>
          </div>
        ) : null,
        attachments: (
          <div style={{ fontSize: 11 }}>
            <strong>Attachments:</strong>{' '}
            {fir.attachments?.length
              ? fir.attachments
                  .map((a) => `${dash(a.file_name)} (${dash(a.file_type)}) ${absFileUrl(a.file_url)}`)
                  .join('; ')
              : 'None'}
          </div>
        ),
        approval: (
          <div style={{ fontSize: 11 }}>
            <p>
              <strong>Approved by:</strong> {dash(fir.approver?.name) || resolve(fir.approved_by)} ·{' '}
              <strong>Approved date:</strong> {fmtDateTime(fir.approved_date) || '—'}
            </p>
            <p>
              <strong>Rejection reason:</strong> {dash(fir.rejection_reason)}
            </p>
          </div>
        ),
      }}
    />
  );
}
